// AboutJamCan.jsx
import React from "react";
import { motion } from "framer-motion";
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { Flame, Leaf, Flag, Instagram } from "lucide-react";

const sectionFade = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const foodImages = [
  "/images/food-wings.jpg",
  "/images/food-bbq.jpg",
  "/images/food-rice.jpg",
  "/images/food-soup.jpg",
  "/images/food-tacos.jpg",
  "/images/food-eggs.jpg",
  "/images/food-fish.jpg",
  "/images/food-veggies.jpg",
];

export default function AboutJamCan() {
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        minHeight: "100vh",
      }}
    >
      {/* 1) Hero Banner */}
      <Box
        component="header"
        sx={{
          position: "relative",
          overflow: "hidden",
          backgroundColor: "#C5271C",
          color: "#fff",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundSize: "cover",
            backgroundPosition: "center",
            zIndex: -2,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.4)",
            zIndex: -1,
          },
        }}
      >
        <Container
          sx={{
            py: { xs: 10, sm: 14 },
            maxWidth: "lg",
            textAlign: "center",
            justifyItems: "center",
          }}
        >
          <Typography
            component={motion.h1}
            variant="h2"
            sx={{ fontWeight: 800, lineHeight: 1.1 }}
          >
            About Us
          </Typography>
          <Typography variant="h6" sx={{ mt: { xs: 2, sm: 3 }, maxWidth: 720 }}>
            Over 20 years of backyard passion bottled for your table.
          </Typography>
        </Container>
      </Box>

      {/* 2) Our Story (Split) */}
      <Box component="section">
        <Container sx={{ py: { xs: 6, sm: 10 }, maxWidth: "lg" }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, lg: 6 }}>
              <Box
                variants={sectionFade}
                initial="hidden"
                component="img"
                src="/images/Jam_Can_Logo_NoBackground.png"
                alt="Bottles of JamCan Hot Pepper Sauce on a wooden table"
                sx={{
                  width: "100%",
                  maxWidth: 400,
                  height: "auto",
                  borderRadius: 2,
                  boxShadow: 3,
                  mx: { xs: "auto", sm: 0 },
                  display: "block",
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
              <Box
                component={motion.div}
                variants={sectionFade}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
              >
                <Typography variant="h4" sx={{ fontWeight: 700 }}>
                  Our Story
                </Typography>
                <Typography
                  sx={{ mt: 2, color: "text.secondary", lineHeight: 1.75 }}
                >
                  I was born in Jamaica and raised in Canada — that’s where the
                  name Jam Can comes from. Over 22 years ago, I started making
                  hot pepper sauce in my backyard. What began as a simple
                  passion has grown into something I’ve been perfecting for more
                  than two decades.
                </Typography>
                <Typography
                  sx={{ mt: 2, color: "text.secondary", lineHeight: 1.75 }}
                >
                  When you open a bottle of Jam Can Hot Pepper Sauce, the aroma
                  hits you right away — sweet fruits mingling with fiery
                  peppers, a fragrance that’s unmistakable. The flavour doesn’t
                  just bring heat; it brings balance. Your mouth waters not
                  because it’s overwhelmingly hot, but because of the layers of
                  flavour packed into every drop.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 3) The JamCan Difference */}
      <Box component="section" sx={{ bgcolor: "grey.75" }}>
        <Container sx={{ py: { xs: 6, sm: 8 }, maxWidth: "lg" }}>
          <Typography
            component={motion.h2}
            variants={sectionFade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            align="center"
            variant="h4"
            sx={{ fontWeight: 700 }}
          >
            Our Philosophy
          </Typography>

          <Typography sx={{ mt: 2, color: "text.secondary", lineHeight: 1.75 }}>
            Unlike many sauces that are just vinegar, water, and pepper, Jam Can
            is built on taste first. It’s bold, vibrant, and versatile — the
            kind of sauce you’ll want in your kitchen at all times. Try it on
            chicken wings, rice, soups, eggs, or use it as a marinade for
            chicken, steak, or fish. It’s perfect for barbecues, and once you’ve
            tried it, you’ll understand why everyone from young to old falls in
            love with it — even those who say they don’t like hot sauce.
          </Typography>

          <Typography sx={{ mt: 2, color: "text.secondary", lineHeight: 1.75 }}>
            Made with 100% natural ingredients, gluten-free, with no
            preservatives or additives, Jam Can Hot Pepper Sauce is as clean as
            it is flavourful. The Scotch Bonnet pepper — the heart of the
            Caribbean — is the star, chosen for its unbeatable taste and aroma.
          </Typography>

          <Typography sx={{ mt: 2, color: "text.secondary", lineHeight: 1.75 }}>
            Every island in the Caribbean has its own style of hot sauce, and
            every family has its own recipe. Jam Can Hot Pepper Sauce is my way
            of bottling up that tradition and giving you a little taste of the
            islands in every bite.
          </Typography>

          <Typography sx={{ mt: 2, color: "text.secondary", lineHeight: 1.75 }}>
            Jam Can Hot Pepper Sauce — Flavour First. Heat Second. Always
            Caribbean.
          </Typography>
        </Container>
      </Box>
      {/* 3) The JamCan Difference */}
      <Box component="section" sx={{ bgcolor: "grey.50" }}>
        <Container sx={{ py: { xs: 6, sm: 8 }, maxWidth: "lg" }}>
          <Typography
            component={motion.h2}
            variants={sectionFade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            align="center"
            variant="h4"
            sx={{ fontWeight: 700 }}
          >
            The JamCan Difference
          </Typography>

          <Grid container spacing={3} sx={{ mt: 3 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <FeatureCard
                icon={<Flame size={22} aria-hidden="true" />}
                title="Flavour First"
                text="Bold, layered taste you’ll reach for every meal—not just a dare."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FeatureCard
                icon={<Leaf size={22} aria-hidden="true" />}
                title="100% Natural"
                text="Clean ingredients, gluten-free, no artificial additives—ever."
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FeatureCard
                icon={<Flag size={22} aria-hidden="true" />}
                title="Authentically Caribbean"
                text="Scotch Bonnet at the heart, crafted with island tradition."
              />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* 4) Experience the Flavour (Lifestyle Showcase) */}
      {/* <Box component="section">
        <Container sx={{ py: { xs: 6, sm: 10 }, maxWidth: "lg" }}>
          <Typography
            component={motion.h2}
            variants={sectionFade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            align="center"
            variant="h4"
            sx={{ fontWeight: 700 }}
          >
            Experience the Flavour
          </Typography>
          <Typography sx={{ mt: 2, color: "text.secondary" }} align="center">
            From wings to soups, rice to marinades — JamCan makes everything
            better.
          </Typography>

          <Grid container spacing={1.5} sx={{ mt: 3 }}>
            {foodImages.map((src, i) => (
              <Grid key={i} size={{ xs: 6, md: 3 }}>
                <Box
                  component={motion.div}
                  variants={sectionFade}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.2 }}
                  sx={{
                    aspectRatio: "1 / 1",
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: 1,
                    backgroundImage: `url('${src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                  role="img"
                  aria-label="Food pairing with JamCan sauce"
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Button
              component={RouterLink}
              to="/recipes"
              variant="outlined"
              sx={{ borderRadius: 2, px: 3, py: 1.2, fontWeight: 600 }}
            >
              Explore Recipes
            </Button>
          </Box>
        </Container>
      </Box> */}

      {/* 5) A Taste of the Islands (Tradition) */}
      {/* <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(5,150,105,0.15), rgba(16,185,129,0.12), rgba(245,158,11,0.15))",
            zIndex: -1,
          },
        }}
      >
        <Container sx={{ py: { xs: 6, sm: 10 }, maxWidth: "lg" }}>
          <Box
            component={motion.div}
            variants={sectionFade}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            sx={{
              borderRadius: 4,
              backdropFilter: "blur(6px)",
              bgcolor: "rgba(255,255,255,0.8)",
              p: { xs: 3, sm: 5 },
              boxShadow: 2,
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              A Taste of the Islands
            </Typography>
            <Typography
              sx={{ mt: 2, color: "text.secondary", lineHeight: 1.75 }}
            >
              Every island has its hot sauce. Every family has its recipe.
              JamCan is our way of bottling up tradition—sun, soil, and
              spice—and sharing it with you. We celebrate the places that shaped
              us and the people we cook for.
            </Typography>

            <Stack
              direction="row"
              spacing={1.5}
              sx={{ mt: 3, flexWrap: "wrap" }}
            >
              <Chip
                icon={<Flag size={16} />}
                label="Jamaica roots"
                sx={{ bgcolor: "grey.100", fontWeight: 600 }}
              />
              <Chip
                icon={<Leaf size={16} />}
                label="Small-batch crafted"
                sx={{ bgcolor: "grey.100", fontWeight: 600 }}
              />
            </Stack>
          </Box>
        </Container>
      </Box> */}

      {/* 6) Closing CTA */}
      {/* <Box
        component="section"
        sx={{
          position: "relative",
          overflow: "hidden",
          color: "#fff",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage: `url('/images/jamcan-bottles-close.jpg')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            zIndex: -2,
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.4)",
            zIndex: -1,
          },
        }}
      >
        <Container
          sx={{ py: { xs: 8, sm: 12 }, textAlign: "center", maxWidth: "lg" }}
        >
          <Typography variant="h4" sx={{ fontWeight: 800 }}>
            Ready to Taste the Heat?
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{ mt: 3 }}
          >
            <Button
              component={RouterLink}
              to="/products"
              variant="contained"
              sx={{
                bgcolor: "warning.main",
                color: "grey.900",
                fontWeight: 700,
                borderRadius: 3,
                px: 3,
                py: 1.2,
                "&:hover": { bgcolor: "warning.light" },
              }}
            >
              Shop Our Sauces
            </Button>
            <Button
              component="a"
              href="https://instagram.com/yourhandle"
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              startIcon={<Instagram size={18} />}
              sx={{
                borderColor: "rgba(255,255,255,0.6)",
                color: "common.white",
                fontWeight: 700,
                borderRadius: 3,
                px: 3,
                py: 1.2,
                backdropFilter: "blur(4px)",
                "&:hover": {
                  borderColor: "common.white",
                  bgcolor: "rgba(255,255,255,0.12)",
                },
              }}
            >
              Follow Us on Instagram
            </Button>
          </Stack>
        </Container>
      </Box> */}
    </Box>
  );
}

function FeatureCard({ icon, title, text }) {
  return (
    <Card
      component={motion.div}
      variants={sectionFade}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.3 }}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: "warning.light",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          aria-hidden
        >
          {icon}
        </Box>
        <Typography variant="h6" sx={{ mt: 2, fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography sx={{ mt: 1.2, color: "text.secondary" }}>
          {text}
        </Typography>
      </CardContent>
    </Card>
  );
}
